import React from 'react';
import { Layers } from 'lucide-react';

export const LadderOfInference: React.FC = () => {
  const rungs = [
    { id: 1, title: 'Data', description: 'Observable facts and information', color: 'primary' },
    { id: 2, title: 'Select', description: 'Choose what data to focus on', color: 'primary' },
    { id: 3, title: 'Interpret', description: 'Add meaning to the data', color: 'secondary' },
    { id: 4, title: 'Assume', description: 'Make assumptions about context', color: 'secondary' },
    { id: 5, title: 'Conclude', description: 'Draw conclusions from assumptions', color: 'accent' },
    { id: 6, title: 'Believe', description: 'Adopt beliefs based on conclusions', color: 'accent' },
    { id: 7, title: 'Act', description: 'Take action based on beliefs', color: 'accent' }
  ];

  const getColorClasses = () => {
    return 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-pri)]';
  };

  return (
    <div className="min-h-screen bg-[var(--surface)] py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Layers className="h-12 w-12 text-consulting-slate-blue-600" />
            <h1 className="text-4xl font-bold text-charcoal-900">Ladder of Inference</h1>
          </div>
          <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
            A cognitive framework for understanding how we move from observable data to action, 
            and how we can question our assumptions at each step.
          </p>
        </div>

        {/* Important Tip */}
        <div className="mb-8 p-6 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <p className="text-[var(--text-pri)] text-lg font-medium">
            "How little data he's drawing massive conclusions from"
          </p>
          <p className="text-[var(--text-sec)] mt-2">
            Most people jump from a small amount of data to big conclusions without examining the steps in between.
          </p>
        </div>

        {/* Main Ladder */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">The Ladder</h2>
          
          <div className="space-y-4">
            {rungs.map((rung, index) => (
              <div key={rung.id} className="relative">
                {/* Connection Line */}
                {index < rungs.length - 1 && (
                  <div className="absolute left-8 top-16 w-0.5 h-8 bg-[var(--rule-bright)] z-0"></div>
                )}
                
                {/* Rung */}
                <div className={`p-6  border-2 ${getColorClasses(rung.color)} relative z-10`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[var(--bg-raised)] border-2 border-current flex items-center justify-center font-bold text-lg">
                      {rung.id}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{rung.title}</h3>
                      <p className="text-sm opacity-80">{rung.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Questions */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Key Questions to Ask</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-charcoal-800 mb-3">At Each Rung:</h3>
              <ul className="space-y-2 text-charcoal-600">
                <li>• <strong>Data:</strong> "What am I actually seeing?"</li>
                <li>• <strong>Select:</strong> "What am I choosing to focus on?"</li>
                <li>• <strong>Interpret:</strong> "What meaning am I adding?"</li>
                <li>• <strong>Assume:</strong> "What am I assuming about context?"</li>
                <li>• <strong>Conclude:</strong> "What conclusions am I drawing?"</li>
                <li>• <strong>Believe:</strong> "What beliefs am I adopting?"</li>
                <li>• <strong>Act:</strong> "What actions am I taking?"</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-charcoal-800 mb-3">Common Assumptions:</h3>
              <ul className="space-y-2 text-charcoal-600">
                <li>• Others see the same data I do</li>
                <li>• My interpretation is the only valid one</li>
                <li>• Past patterns predict future outcomes</li>
                <li>• My assumptions are based on facts</li>
                <li>• My conclusions are logical and sound</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Usage Examples */}
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8 mb-8">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-6">When to Use This Tool</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Coaching</h3>
              <p className="text-[var(--text-sec)] text-sm">Help clients see how they jump to conclusions and question their assumptions.</p>
            </div>
            <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Team Conflict</h3>
              <p className="text-[var(--text-sec)] text-sm">Understand how different people can see the same situation differently.</p>
            </div>
            <div className="p-4 bg-[var(--surface)] border border-[var(--rule)]">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Decision Making</h3>
              <p className="text-[var(--text-sec)] text-sm">Slow down and examine the reasoning behind important decisions.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
